'use client'

import { useContext } from 'react'
import { TmsContext } from './tmsProvider'
import { TmsContextType } from './tmsProvider.types'

/**
 * Custom hook to access the TMS context
 * @returns TmsContextType - The TMS context object with data layer and tracking functions
 */
export const useTms = (): TmsContextType => {
  const context = useContext(TmsContext)

  if (!context) {
    throw new Error('useTms must be used within a TmsProvider')
  }

  return context
}

/**
 * Custom hook that exposes easy tracking functions.
 * Components should use THIS hook instead of calling updateDataLayer directly.
 * @returns Object with tracking functions: trackView, trackLink, getUtagData, getAllEvents
 */
export const useTmsTracking = () => {
  const { updateDataLayer } = useTms()

  const trackView = (eventData: Record<string, any>) => {
    const { event_name, ...restData } = eventData
    updateDataLayer('view', event_name, restData)
  }

  const trackLink = (eventData: Record<string, any>) => {
    const { event_name, ...restData } = eventData
    updateDataLayer('link', event_name, restData)
  }

  const getUtagData = () => {
    if (typeof window !== 'undefined' && (window as any).utag_data) {
      return (window as any).utag_data
    }
    return {}
  }

  const getAllEvents = () => {
    if (typeof window !== 'undefined' && (window as any).tmsevents) {
      return (window as any).tmsevents
    }
    return []
  }

  return {
    trackView,
    trackLink,
    getUtagData,
    getAllEvents
  }
}
